import { useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Check } from "lucide-react";

const Pricing = () => {
  const [billingCycle, setBillingCycle] = useState<"monthly" | "yearly">("monthly");

  const pricingData = {
    monthly: [
      {
        name: "Basic",
        description: "Essential features for beginners",
        price: "$19",
        priceDescription: "per month",
        features: [
          "List up to 5 backlink offers",
          "Receive backlink requests",
          "Basic analytics",
          "Email support",
        ],
        cta: "Get Started",
        highlighted: false,
      },
      {
        name: "Pro",
        description: "Perfect for active sellers",
        price: "$49",
        priceDescription: "per month",
        features: [
          "List up to 20 backlink offers",
          "Receive unlimited requests",
          "Priority placement in search",
          "Detailed performance analytics",
          "Priority email support",
          "Verified seller badge",
        ],
        cta: "Upgrade to Pro",
        highlighted: true,
      },
      {
        name: "Business",
        description: "For agencies and power users",
        price: "$99",
        priceDescription: "per month",
        features: [
          "Unlimited backlink offers",
          "Dedicated account manager",
          "API access",
          "White label reports",
          "Team accounts",
          "Custom integrations",
          "24/7 phone support",
        ],
        cta: "Contact Sales",
        highlighted: false,
      },
    ],
    yearly: [
      {
        name: "Basic",
        description: "Essential features for beginners",
        price: "$179",
        priceDescription: "per year",
        features: [
          "List up to 5 backlink offers",
          "Receive backlink requests",
          "Basic analytics",
          "Email support",
        ],
        cta: "Get Started",
        highlighted: false,
        savings: "Save $49",
      },
      {
        name: "Pro",
        description: "Perfect for active sellers",
        price: "$469",
        priceDescription: "per year",
        features: [
          "List up to 20 backlink offers",
          "Receive unlimited requests",
          "Priority placement in search",
          "Detailed performance analytics",
          "Priority email support",
          "Verified seller badge",
        ],
        cta: "Upgrade to Pro",
        highlighted: true,
        savings: "Save $119",
      },
      {
        name: "Business",
        description: "For agencies and power users",
        price: "$949",
        priceDescription: "per year",
        features: [
          "Unlimited backlink offers",
          "Dedicated account manager",
          "API access",
          "White label reports",
          "Team accounts",
          "Custom integrations",
          "24/7 phone support",
        ],
        cta: "Contact Sales",
        highlighted: false,
        savings: "Save $239",
      },
    ],
  };

  const faqs = [
    {
      question: "Do I need a paid plan to buy backlinks?",
      answer:
        "No, anyone can browse and purchase backlinks without a subscription. Paid plans are for sellers who want to list their backlink opportunities on our marketplace.",
    },
    {
      question: "Can I upgrade or downgrade my plan later?",
      answer:
        "Yes, you can upgrade or downgrade your plan at any time. When upgrading, you'll be charged the prorated difference. When downgrading, your new rate will apply at the next billing cycle.",
    },
    {
      question: "Is there a free trial?",
      answer:
        "Yes, we offer a 14-day free trial on all plans. No credit card required to start your trial.",
    },
    {
      question: "What payment methods do you accept?",
      answer:
        "We accept all major credit cards, PayPal, and bank transfers for annual plans.",
    },
    {
      question: "What's your refund policy?",
      answer:
        "We offer a 30-day money-back guarantee on all plans. If you're not satisfied, contact us for a full refund.",
    },
  ];

  return (
    <>
      <Helmet>
        <title>Pricing Plans | SEO Link Market</title>
      </Helmet>
      <div className="bg-gray-50 py-12 md:py-20">
        <div className="container mx-auto px-4">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <h1 className="text-4xl font-bold mb-4">Simple, transparent pricing</h1>
            <p className="text-xl text-gray-600">
              Choose the plan that's right for your backlink business. All plans include a 14-day free trial.
            </p>
          </div>

          <div className="flex justify-center mb-10">
            <Tabs
              defaultValue="monthly"
              value={billingCycle}
              onValueChange={(value) => setBillingCycle(value as "monthly" | "yearly")}
              className="w-full max-w-md"
            >
              <TabsList className="grid w-full grid-cols-2 mb-10">
                <TabsTrigger value="monthly">Monthly</TabsTrigger>
                <TabsTrigger value="yearly">
                  Yearly <span className="ml-1 text-xs font-semibold text-green-500">Save up to 20%</span>
                </TabsTrigger>
              </TabsList>
            </Tabs>
          </div>

          <div className="grid gap-8 md:grid-cols-3 max-w-6xl mx-auto">
            {pricingData[billingCycle].map((plan, index) => (
              <Card
                key={index}
                className={`flex flex-col ${
                  plan.highlighted
                    ? "shadow-lg border-brand-blue relative"
                    : "shadow-md border-gray-200"
                }`}
              >
                {plan.highlighted && (
                  <div className="absolute top-0 inset-x-0 transform -translate-y-1/2">
                    <div className="inline-block bg-brand-blue text-white text-sm font-semibold py-1 px-4 rounded-full">
                      Most Popular
                    </div>
                  </div>
                )}
                <CardHeader>
                  <CardTitle className="text-2xl">{plan.name}</CardTitle>
                  <CardDescription>{plan.description}</CardDescription>
                  <div className="mt-4">
                    <span className="text-4xl font-bold">{plan.price}</span>
                    <span className="text-gray-500 ml-2">{plan.priceDescription}</span>
                    {plan.savings && (
                      <span className="block mt-1 text-sm font-medium text-green-500">
                        {plan.savings}
                      </span>
                    )}
                  </div>
                </CardHeader>
                <CardContent className="flex-grow">
                  <ul className="space-y-3">
                    {plan.features.map((feature, i) => (
                      <li key={i} className="flex items-center">
                        <Check className="h-5 w-5 text-green-500 mr-2 shrink-0" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
                <CardFooter>
                  <Button
                    className={`w-full ${
                      plan.highlighted
                        ? "bg-brand-blue hover:bg-brand-darkBlue"
                        : ""
                    }`}
                    variant={plan.highlighted ? "default" : "outline"}
                    asChild
                  >
                    <Link to="/signup">{plan.cta}</Link>
                  </Button>
                </CardFooter>
              </Card>
            ))}
          </div>

          <div className="mt-24 max-w-3xl mx-auto">
            <h2 className="text-3xl font-bold text-center mb-8">Frequently Asked Questions</h2>
            <div className="space-y-6">
              {faqs.map((faq, index) => (
                <div key={index} className="border-b border-gray-200 pb-6">
                  <h3 className="text-xl font-semibold mb-2">{faq.question}</h3>
                  <p className="text-gray-600">{faq.answer}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-md p-8 mt-16 max-w-4xl mx-auto text-center">
            <h2 className="text-2xl font-bold mb-4">Need a custom plan?</h2>
            <p className="text-gray-600 mb-6">
              Contact our sales team for enterprise pricing and custom solutions for larger teams and agencies.
            </p>
            <Button size="lg" className="bg-brand-blue hover:bg-brand-darkBlue">
              Contact Sales
            </Button>
          </div>
        </div>
      </div>
    </>
  );
};

export default Pricing; 